import { LEVEL_CEILING, MAX_LEVEL, STAT_CAP, STAT_CAP_MAX } from './constants';

/**
 * Coût L→L+1 jusqu'au niveau 60 : la forme du dossier §4 (100 + 25L + 4L²) divisée par XP_CURVE_DIVISOR et arrondie à l'unité.
 * Léni a choisi d'adoucir la courbe (2026-11-03) ; le diviseur est calibré avec scripts/progression-bench.ts
 * pour que le niveau 60 demande environ 26 min de jeu vivant en farm sans boost (cible du dossier : 20–30 min).
 */
export const XP_CURVE_DIVISOR = 8;

/**
 * Au-delà du niveau 60 (arène libre seulement, niveau 250) : coût linéaire POST_60.base + POST_60.slope × (L − 60).
 * Choix de Léni (5 novembre 2026) : niveau 250 en environ 1 h 30 de vie, avec un rythme qui ralentit (environ 8 s par
 * niveau juste après 60, environ 35 s vers 250). Calibré avec scripts/progression-bench.ts (docs/decisions.md).
 */
export const POST_60 = { base: 120, slope: 2.13 };

export function xpCostToNext(level: number): number {
  if (level >= MAX_LEVEL) return Math.round(POST_60.base + POST_60.slope * (level - MAX_LEVEL));
  return Math.round((100 + 25 * level + 4 * level * level) / XP_CURVE_DIVISOR);
}

/** XP cumulée nécessaire pour atteindre le niveau L : somme des coûts des niveaux 1 à L−1, C(1) = 0. */
export function xpForLevel(level: number): number {
  let sum = 0;
  for (let l = 1; l < Math.floor(level); l++) sum += xpCostToNext(l);
  return sum;
}

const TABLE: number[] = [];
for (let l = 1; l <= LEVEL_CEILING; l++) TABLE.push(xpForLevel(l));

/**
 * Niveau atteint pour une XP cumulée, plafonné au niveau maximal du mode (60 par défaut, 250 en arène libre) ;
 * l'XP continue au-delà pour le score.
 */
export function levelForXp(xp: number, cap: number = MAX_LEVEL): number {
  let lo = 1;
  let hi = Math.max(1, Math.min(LEVEL_CEILING, Math.floor(cap)));
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (TABLE[mid - 1] <= xp) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Fraction de progression vers le niveau suivant (1 au niveau max du mode). */
export function levelProgress(xp: number, cap: number = MAX_LEVEL): number {
  const top = Math.max(1, Math.min(LEVEL_CEILING, Math.floor(cap)));
  const l = levelForXp(xp, top);
  if (l >= top) return 1;
  const a = TABLE[l - 1];
  const b = TABLE[l];
  return (xp - a) / (b - a);
}

/**
 * Points de statistique : un par niveau de 2 à 60 (59), puis un tous les 2 niveaux au-delà (choix de Léni,
 * 5 novembre 2026) : 154 au niveau 250.
 */
export function statPointsForLevel(level: number): number {
  const l = Math.min(level, LEVEL_CEILING);
  return Math.max(0, Math.min(l, MAX_LEVEL) - 1) + Math.floor(Math.max(0, l - MAX_LEVEL) / 2);
}

/**
 * Plafond par statistique : 10 jusqu'au niveau 60, puis +1 tous les 19 niveaux, 20 au niveau 250. Monter peu à peu
 * garde le début de partie comme avant (sinon un niveau 30 pourrait mettre 20 points dans une seule stat).
 */
export function statCapForLevel(level: number): number {
  const over = Math.max(0, Math.min(level, LEVEL_CEILING) - MAX_LEVEL);
  return Math.min(STAT_CAP_MAX, STAT_CAP + Math.floor(over / 19));
}

/** Emplacements de modifications débloqués (dossier §11, puis niveau 250 en arène libre). */
export const MOD_SLOT_LEVELS = {
  weapon1: 15,
  chassis1: 20,
  passive: 35,
  weapon2: 45,
  chassis2: 50,
  weapon3: 80,
  chassis3: 110,
  passive2: 140,
  weapon4: 170,
  chassis4: 200,
} as const;
